/**
 * Self-serve dashboard service — wraps /api/v1/(investors|companies)/me/*.
 *
 * Used by external users (investors, founders) to see their own data.
 * No team membership required; the backend scopes by User.investor_id /
 * User.company_id.
 */
import api from './api';

export interface ServiceResult<T> {
  data?: T;
  error?: string;
}

function extractError(err: any, fallback: string): string {
  return (
    err?.response?.data?.message ||
    err?.response?.data?.detail ||
    err?.message ||
    fallback
  );
}

export interface SelfServeInvestor {
  id: number;
  full_name: string | null;
  firm_name: string | null;
  email: string | null;
  investor_type: string | null;
  stage_preferences: string[] | null;
  sector_focus: string[] | null;
  check_size_min: number | null;
  check_size_max: number | null;
  investment_philosophy: string | null;
}

export interface SelfServeCompany {
  id: number;
  name: string | null;
  industry: string | null;
  sub_industry: string | null;
  description: string | null;
  funding_stage: string | null;
  funding_amount_sought: number | null;
  team_size: number | null;
  headquarters: string | null;
  is_currently_raising: boolean | null;
  ai_company_dossier: string | null;
}

export interface SelfServeMatch {
  id: number;
  overall_score: number | null;
  dimension_scores: Record<string, number | null> | null;
  status: string | null;
  match_type: string | null;
  investor_id: number | null;
  startup_id: number | null;
  project_id: number | null;
  counterparty_name: string | null;
  counterparty_kind: string | null;
  recommended_instrument: string | null;
  approach_strategy: string | null;
  talking_points: string[] | null;
  ai_review_headline: string | null;
  ai_review_verdict: string | null;
}

export interface CRSPreview {
  id: number;
  overall_score: number | null;
  tier: string | null;
  dimension_scores: Record<string, number> | null;
  generated_at: string | null;
  // Paywall — basic score is free; gap_analysis + improvement_roadmap
  // unlock when the company has paid for the assessment via Stripe.
  unlocked: boolean;
  paid_at: string | null;
  gap_analysis: Record<string, any> | null;
  improvement_roadmap: Record<string, any> | null;
}

export interface FounderCompanySubmit {
  name: string;
  website?: string;
  industry?: string;
  description?: string;
  funding_stage?: string;
  funding_amount_sought?: number;
  team_size?: number;
  headquarters?: string;
  contact_name?: string;
  pitch_deck_url?: string;
}

export interface PartnerNetworkRecord {
  kind: 'investor' | 'company';
  id: number;
  name: string | null;
  secondary: string | null;
  research_completion_pct: number | null;
  created_at: string | null;
}

export interface PartnerNetworkResponse {
  investor_count: number;
  company_count: number;
  records: PartnerNetworkRecord[];
}

export interface PaymentsStatus {
  enabled: boolean;
  crs_assessment_price_cents: number;
  crs_assessment_product_name: string;
}

export interface CheckoutResponse {
  session_id: string;
  url: string;
  already_paid: boolean;
}

export async function getMyInvestor(): Promise<ServiceResult<SelfServeInvestor>> {
  try {
    const { data } = await api.get<SelfServeInvestor>('/investors/me');
    return { data };
  } catch (err) {
    return { error: extractError(err, 'Failed to load investor profile') };
  }
}

export async function listMyInvestorMatches(): Promise<ServiceResult<SelfServeMatch[]>> {
  try {
    const { data } = await api.get<SelfServeMatch[]>('/investors/me/matches');
    return { data };
  } catch (err) {
    return { error: extractError(err, 'Failed to load matches') };
  }
}

export async function getMyInvestorMatch(id: number): Promise<ServiceResult<SelfServeMatch>> {
  try {
    const { data } = await api.get<SelfServeMatch>(`/investors/me/matches/${id}`);
    return { data };
  } catch (err) {
    return { error: extractError(err, 'Failed to load match') };
  }
}

export async function getMyCompany(): Promise<ServiceResult<SelfServeCompany>> {
  try {
    const { data } = await api.get<SelfServeCompany>('/companies/me');
    return { data };
  } catch (err) {
    return { error: extractError(err, 'Failed to load company profile') };
  }
}

export async function listMyCompanyMatches(): Promise<ServiceResult<SelfServeMatch[]>> {
  try {
    const { data } = await api.get<SelfServeMatch[]>('/companies/me/matches');
    return { data };
  } catch (err) {
    return { error: extractError(err, 'Failed to load matches') };
  }
}

export async function getMyCompanyCRS(): Promise<ServiceResult<CRSPreview>> {
  try {
    const { data } = await api.get<CRSPreview>('/companies/me/capital-readiness');
    return { data };
  } catch (err) {
    return { error: extractError(err, 'Failed to load CRS') };
  }
}

export interface CRSShareLink {
  company_id: number;
  url: string;
  token: string;
}

export async function mintMyCRSShareLink(): Promise<ServiceResult<CRSShareLink>> {
  try {
    const { data } = await api.get<CRSShareLink>('/companies/me/share-link/crs');
    return { data };
  } catch (err) {
    return { error: extractError(err, 'Failed to generate share link') };
  }
}

export interface PublicCRSPreview {
  company_id: number;
  company_name: string | null;
  industry: string | null;
  overall_score: number | null;
  tier: string | null;
  dimension_scores: Record<string, number> | null;
  generated_at: string | null;
}

export async function getPublicCRSPreview(
  companyId: number, token: string,
): Promise<ServiceResult<PublicCRSPreview>> {
  try {
    const { data } = await api.get<PublicCRSPreview>(
      `/share/crs/${companyId}`, { params: { t: token } },
    );
    return { data };
  } catch (err) {
    return { error: extractError(err, 'Preview not found') };
  }
}

export async function submitMyCompany(
  payload: FounderCompanySubmit,
): Promise<ServiceResult<SelfServeCompany>> {
  try {
    const { data } = await api.post<SelfServeCompany>(
      '/companies/me/submit', payload,
    );
    return { data };
  } catch (err) {
    return { error: extractError(err, 'Failed to submit company') };
  }
}

export async function getMyPartnerNetwork(): Promise<ServiceResult<PartnerNetworkResponse>> {
  try {
    const { data } = await api.get<PartnerNetworkResponse>('/partner/network');
    return { data };
  } catch (err) {
    return { error: extractError(err, 'Failed to load partner network') };
  }
}

export async function getPaymentsStatus(): Promise<ServiceResult<PaymentsStatus>> {
  try {
    const { data } = await api.get<PaymentsStatus>('/payments/status');
    return { data };
  } catch (err) {
    return { error: extractError(err, 'Failed to load payments status') };
  }
}

export interface InvestorMatchFeedback {
  note: string;
  sentiment?: 'agree' | 'more_like_this' | 'fewer_like_this';
}

export async function addMyInvestorMatchFeedback(
  payload: InvestorMatchFeedback,
): Promise<ServiceResult<{ ok: boolean; saved_at: string; sentiment?: string | null }>> {
  try {
    const { data } = await api.post('/investors/me/match-feedback', payload);
    return { data };
  } catch (err) {
    return { error: extractError(err, 'Failed to save feedback') };
  }
}

export async function startCRSCheckout(): Promise<ServiceResult<CheckoutResponse>> {
  try {
    const { data } = await api.post<CheckoutResponse>('/payments/crs/checkout');
    return { data };
  } catch (err) {
    return { error: extractError(err, 'Failed to start checkout') };
  }
}

const selfServeService = {
  getMyInvestor,
  listMyInvestorMatches,
  getMyInvestorMatch,
  getMyCompany,
  listMyCompanyMatches,
  getMyCompanyCRS,
  mintMyCRSShareLink,
  getPublicCRSPreview,
  submitMyCompany,
  getMyPartnerNetwork,
  getPaymentsStatus,
  startCRSCheckout,
  addMyInvestorMatchFeedback,
};

export default selfServeService;
